// Клавиатурата на таблото — слушател на прозореца, докато таблото е отворено:
//   • 1–8 отваря операцията на N-тата кутия (отвсякъде, освен от поле за писане);
//   • стрелките местят фокуса между кутиите като в решетка (колоните идват от
//     ширината — 3 / 2 / 1), Home/End — първата/последната; от бутон ВЪТРЕ в
//     кутия стрелката пак скача до съседната кутия;
//   • стрелка „отникъде" (фокусът е на body) → първата кутия;
//   • Enter е самият бутон на кутията (родно поведение, без код тук).
// Фокусът в менюто или в друг елемент извън решетката — стрелките са негови.
// При отворен antd слой (въпросът за черновата) клавишите са на слоя.
import { useEffect, useRef, type RefObject } from "react";
import { hasOpenOverlay, isEditableTarget } from "../../../../lib/overlay";
import { boxIndexOfKey, neighborBoxIndex } from "../dashboardModel";

const NAVIGATION_KEYS = new Set(["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Home", "End"]);

type Options = {
  gridRef: RefObject<HTMLElement | null>;
  columns: number;
  total: number;
  onOpenIndex: (index: number) => void;
};

export const useDashboardKeyboard = ({ gridRef, columns, total, onOpenIndex }: Options) => {
  // Последните стойности — без да се закача слушателят наново на всеки рендер
  const latest = useRef({ columns, total, onOpenIndex });
  useEffect(() => {
    latest.current = { columns, total, onOpenIndex };
  });

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || hasOpenOverlay() || isEditableTarget(event.target)) return;
      const current = latest.current;

      const digitIndex = boxIndexOfKey(event, current.total);
      if (digitIndex != null) {
        event.preventDefault();
        if (!event.repeat) current.onOpenIndex(digitIndex);
        return;
      }

      if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;
      if (!NAVIGATION_KEYS.has(event.key)) return;
      const grid = gridRef.current;
      if (!grid) return;
      const openButtons = Array.from(grid.querySelectorAll<HTMLElement>("[data-dash-open]"));
      if (!openButtons.length) return;

      const active = document.activeElement;
      let target: number | null;
      if (active instanceof HTMLElement && grid.contains(active)) {
        const box = active.closest("[data-dash-box]");
        const index = box ? openButtons.findIndex((button) => box.contains(button)) : -1;
        if (index < 0) return;
        target = neighborBoxIndex(index, event.key, current.columns, openButtons.length);
      } else if (!active || active === document.body) {
        target = event.key === "End" ? openButtons.length - 1 : 0;
      } else {
        return;
      }
      // И на ръба (няма накъде) клавишът е наш — иначе страницата се превърта
      event.preventDefault();
      if (target != null) openButtons[target]?.focus();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [gridRef]);
};
